// frontend/src/components/UI/ConsentModal.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import Modal from './Modal';

/**
 * PDPA Consent Modal
 * 
 * Displays on first login when hrprofile_consent_obtained is false.
 * Cannot be dismissed - user must accept to proceed.
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onAccept: function - Callback when user accepts consent
 * - loading: boolean - Whether accept request is in progress
 * - companyName: string - Company name to display (optional)
 */
const ConsentModal = ({ 
  isOpen, 
  onAccept, 
  loading = false,
  companyName = 'the Company'
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (acknowledged && !loading) {
      onAccept();
    }
  };

  const header = (
    <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-6 sm:px-8">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
          <Shield className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            Privacy Policy Acknowledgement
          </h2>
          <p className="text-blue-100 text-sm mt-1">
            Personal Data Protection Act (PDPA) Compliance
          </p>
        </div>
      </div>
    </div>
  );

  const modalFooter = (
    <div className="space-y-3">
      <button
        onClick={handleAccept}
        disabled={!acknowledged || loading}
        className={`w-full py-3 px-6 rounded-full font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
          ${acknowledged && !loading 
            ? 'bg-blue-900 hover:bg-blue-800 shadow-lg hover:shadow-xl' 
            : 'bg-gray-300 cursor-not-allowed'
          }`}
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CheckCircle className="w-5 h-5" />
            I Acknowledge & Accept
          </>
        )}
      </button>
      <p className="text-xs text-gray-500 text-center">
        You must acknowledge the Privacy Policy to continue using the HR Portal.
      </p>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      title="Privacy Policy Acknowledgement"
      size="md"
      closable={false}
      customHeader={header}
      footer={modalFooter}
    >
      <p className="text-gray-700 leading-relaxed">
        Welcome to the HR Portal. Before you proceed, please review and acknowledge our 
        Privacy Policy in accordance with the <strong>Singapore Personal Data Protection Act (PDPA)</strong>.
      </p>

      <div className="mt-6 bg-gray-50 rounded-xl p-5 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          What We Collect & Why
        </h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span><strong>Personal identification</strong> (name, NRIC/FIN, passport) for employment records and statutory compliance</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span><strong>Contact information</strong> (including emergency contact details: name, phone, email of your designated contacts) for work communication and emergency purposes</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span><strong>Financial details</strong> (bank account, salary) for payroll processing and CPF contributions</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span><strong>Employment records</strong> for HR administration, leave management, and attendance tracking</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span><strong>Leave calendar sharing</strong> - Limited leave information (name, department, dates, generic leave type) shared with colleagues in department and company calendars for workplace coordination and planning</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <span><strong>Family/dependent information</strong> for statutory leave entitlements (childcare, maternity, paternity)</span>
          </li>
        </ul>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-gray-700">
            <strong>Note:</strong> When providing emergency contact information, you confirm that you have obtained consent from your designated emergency contacts to store their personal information (name, phone number, email address) for emergency purposes.
          </p>
        </div>
      </div>

      <div className="mt-5 bg-blue-50 rounded-xl p-5 border border-blue-200">
        <h3 className="font-semibold text-gray-900 mb-2">Your Rights</h3>
        <p className="text-sm text-gray-700">
          You have the right to <strong>access</strong> and <strong>correct</strong> your personal data 
          at any time through the My Profile page. You may also <strong>withdraw consent</strong> by 
          contacting HR, though this may affect our ability to fulfill employment obligations.
        </p>
      </div>

      <div className="mt-5">
        <Link 
          to="/privacy-policy" 
          target="_blank"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Read Full Privacy Policy
        </Link>
      </div>

      <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="w-5 h-5 accent-blue-700 text-blue-700 border-gray-300 rounded focus-visible:ring-blue-500 mt-0.5 cursor-pointer"
          />
          <span className="text-sm text-gray-700">
            I have read and understood the Privacy Policy. I acknowledge that {companyName} may 
            collect, use, and disclose my personal data for the purposes stated above in accordance 
            with the PDPA.
          </span>
        </label>
      </div>
    </Modal>
  );
};

export default ConsentModal;



